import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from '../contexts/UserContext';
import { useUserDBContext } from '../contexts/UserDBContext';
import { updatePassword } from '@aws-amplify/auth';
import { Breadcrumbs } from '@aws-amplify/ui-react';
import { MdChevronRight } from 'react-icons/md';
import axios from "axios";
import LoadingLogo from '../components/LoadingLogo';
import {
  CognitoIdentityProviderClient,
  UpdateUserAttributesCommand,
  VerifyUserAttributeCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import './style/Profile.css';
import { useTranslation } from 'react-i18next';
import { requestReferralCode } from '../stripe/RequestReferralCode';

const endpoint = process.env.REACT_APP_API_URL + "/cancel-stripe-subscription";

function Profile() {
  const { userInfo, tokens } = useUserContext();
  const { userDBData, loadingUserDB, errorUserDB } = useUserDBContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [editEmail, setEditEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [editPassword, setEditPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralMessage, setReferralMessage] = useState('');
  const [referralCodeLocal, setReferralCodeLocal] = useState('');
  


  const client = new CognitoIdentityProviderClient({ region: 'eu-west-1' });

  const handleEmailChange = async () => {
    setEmailMessage('');
    if (!newEmail || !newEmail.includes('@')) {
      setEmailMessage(t('profilepage.error.invalidEmail'));
      return;
    }
    try {
      const command = new UpdateUserAttributesCommand({
        AccessToken: tokens.accessToken,
        UserAttributes: [{ Name: 'email', Value: newEmail }],
      });
      await client.send(command);
      setEmailMessage(t('profilepage.emailUpdateSuccess'));
      setEmailConfirmed(false);
    } catch (err) {
      setEmailMessage(`❌ ${t('profilepage.error.updateEmail')} ${err.message}`);
    }
  };

  const handleEmailConfirm = async () => {
    try {
      const command = new VerifyUserAttributeCommand({
        AccessToken: tokens.accessToken,
        AttributeName: 'email',
        Code: confirmationCode,
      });
      await client.send(command);
      setEmailMessage(t('profilepage.emailConfirmed'));
      setEmailConfirmed(true);
      setEditEmail(false);
    } catch (err) {
      setEmailMessage(`❌ ${t('profilepage.error.invalidCode')} ${err.message}`);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordMessage('');
    try {
      await updatePassword({ oldPassword, newPassword });
      setPasswordMessage(t('profilepage.passwordUpdateSuccess'));
      setEditPassword(false);
    } catch (err) {
      setPasswordMessage(`❌ ${t('profilepage.error.updatePassword')} ${err.message}`);
    }
  };

  const handleChangeSubscription = () => {
    navigate('/pricing');
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
    setCancelSuccess(false);
    setCancelError(null);
  };



  const handleConfirmCancel = async () => {
    setCancelLoading(true);
    setCancelError(null);
    setCancelSuccess(false);

    try {
      await axios.post(endpoint, { username: userInfo.username }, {
        headers: { Authorization: `Bearer ${tokens.accessToken}` }
      });
      setCancelSuccess(true);
    } catch (err) {
      setCancelError(err.response?.data?.error || err.message || t('profilepage.cancelErrorDefault'));
    } finally {
      setCancelLoading(false);
      setShowCancelModal(false);
    }
  };

  const handleAskReferral = async () => {
    setReferralMessage('');
    setReferralLoading(true);
    try {
      const code = await requestReferralCode({
        accessToken: tokens.accessToken,
        username: userInfo?.username,
      });
      setReferralCodeLocal(code); // on l’affiche immédiatement sans attendre un refresh DB
      setReferralMessage(t('profilepage.referral.success', '✅ Code généré avec succès.'));
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || t('profilepage.referral.errorDefault', 'Une erreur est survenue.');
      setReferralMessage(`❌ ${msg}`);
    } finally {
      setReferralLoading(false);
    }
  };



  if (loadingUserDB) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <LoadingLogo />
      </div>
    );
  }

  return (
    <>
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>{t('profilepage.modal.title')}</h3>
            <p>
              {t('profilepage.modal.text1')} <b>{t('profilepage.modal.text2')}</b> {t('profilepage.modal.text3')}
            </p>
            <div className="modal-buttons">
              <button onClick={() => setShowCancelModal(false)} className="cancel-button" disabled={cancelLoading}>
                {t('profilepage.modal.cancel')}
              </button>
              <button
                className="confirm-button"
                onClick={handleConfirmCancel}
                disabled={cancelLoading}
                style={cancelLoading ? { opacity: 0.7, pointerEvents: "none" } : {}}
              >
                {cancelLoading ? (
                  <span style={{ display: "inline-block", verticalAlign: "middle" }}>
                    <span className="spinner"
                      style={{
                        display: "inline-block",
                        width: 18,
                        height: 18,
                        border: "2px solid #fff",
                        borderRightColor: "transparent",
                        borderRadius: "50%",
                        animation: "spin 0.7s linear infinite",
                        marginRight: 7
                      }}
                    />
                    {t('profilepage.modal.loading')}
                  </span>
                ) : (
                  t('profilepage.modal.confirm')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}
      </style>

      <div style={{ marginTop: '10px', marginLeft: '10%' }}>
        <Breadcrumbs
          items={[{ label: '' }, { label: t('profilepage.breadcrumb') }]}
          separator={<MdChevronRight style={{ color: '#facc15' }} />}
        />
      </div>

      <div className="profile-page">
        <div className="profile-page-container">
          <h2>👤 {t('profilepage.title')}</h2>

          {errorUserDB && <p className="error-message">{errorUserDB}</p>}

          {userDBData && (
            <div className="profile-info">
              <p><strong>{t('profilepage.username')}</strong> {userInfo?.displayName || userInfo?.username}</p>
              {userInfo?.provider === 'CognitoGoogle' && (
                <p><strong>{t('profilepage.googleName')}</strong> {userInfo?.username}</p>
              )}

              <p>
                <strong>{t('profilepage.email')}</strong> {userInfo.email}
                <button className="action-button" onClick={() => setEditEmail(!editEmail)}>
                  {t('profilepage.editEmail')}
                </button>
              </p>
              {editEmail && (
                <div className="edit-section">
                  <input type="email" placeholder={t('profilepage.newEmail')} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                  <button className="confirm-button" onClick={handleEmailChange}>{t('profilepage.updateEmail')}</button>
                  {!emailConfirmed && (
                    <>
                      <input type="text" placeholder={t('profilepage.confirmCode')} value={confirmationCode} onChange={(e) => setConfirmationCode(e.target.value)} />
                      <button className="confirm-button" onClick={handleEmailConfirm}>{t('profilepage.confirmBtn')}</button>
                    </>
                  )}
                  {emailMessage && <p className="info-message">{emailMessage}</p>}
                </div>
              )}

              <p>
                <strong>{t('profilepage.password')}</strong> ******
                <button className="action-button" onClick={() => setEditPassword(!editPassword)}>
                  {t('profilepage.editPassword')}
                </button>
              </p>
              {editPassword && (
                <div className="edit-section">
                  <input type="password" placeholder={t('profilepage.oldPassword')} value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
                  <input type="password" placeholder={t('profilepage.newPassword')} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                  <button className="confirm-button" onClick={handlePasswordChange}>{t('profilepage.updatePassword')}</button>
                  {passwordMessage && <p className="info-message">{passwordMessage}</p>}
                </div>
              )}

              <p>
                <strong>{t('profilepage.subscription')}</strong> {userDBData.subscriptions}
                {userDBData.subscriptions_start_date && (
                  <span style={{ marginLeft: 10, fontWeight: 400, fontSize: "0.97em", color: "#ccc" }}>
                    | {t('profilepage.subscribedOn')} {userDBData.subscriptions_start_date.split(" ")[0]}
                  </span>
                )}
                <button className="change-sub-button" onClick={handleChangeSubscription} style={{ marginLeft: 18, verticalAlign: "middle" }}>
                  {t('profilepage.change')}
                </button>
              </p>

              {/* ---- Code de parrainage ---- */}
              <p style={{ marginTop: 8 }}>
                <strong>{t('profilepage.referral.title', 'Code de parrainage')}</strong>{' : '}
                {(() => {
                  const codeFromDB = userDBData?.referral_code;
                  const codeToShow = referralCodeLocal || codeFromDB;
                  const custId = userDBData?.stripe_customer_id;

                  if (codeToShow) {
                    return <code style={{ fontWeight: 700 }}>{codeToShow}</code>;
                  }
                  if (!custId) {
                    return <i>{t('profilepage.referral.needPurchase', 'Veuillez effectuer un achat')}</i>;
                  }
                  return (
                    <button
                      className="confirm-button"
                      onClick={handleAskReferral}
                      disabled={referralLoading}
                      style={referralLoading ? { opacity: 0.7, pointerEvents: 'none' } : {}}
                    >
                      {referralLoading
                        ? t('profilepage.referral.loading', 'Génération...')
                        : t('profilepage.referral.askCode', 'Demander mon code')}
                    </button>
                  );
                })()}
              </p>
              {referralMessage && (
                <div className="info-message" style={{ marginTop: 6 }}>
                  {referralMessage}
                </div>
              )}
              {/* ---- Fin code de parrainage ---- */}

              {["monthly", "annual"].includes(userDBData.subscriptions_type) && (
                <p style={{ marginTop: -8 }}>
                  {t('profilepage.nextRenewal')} <b>{userDBData.subscriptions_end_date}</b>
                  <button className="cancel-sub-button" style={{ background: "#f87171", color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontWeight: 700, cursor: "pointer", marginLeft: 18, verticalAlign: "middle" }} onClick={handleCancelClick}>
                    {t('profilepage.cancel')}
                  </button>
                </p>
              )}

              {userDBData.subscriptions_end_date && (
                <div style={{ color: "#f87171", marginTop: 4, fontWeight: 500, fontSize: 14 }}>
                  {t('profilepage.expiredOn')} <b>{userDBData.subscriptions_end_date}</b>
                </div>
              )}

              {cancelSuccess && (
                <div className="info-message" style={{ color: "#22c55e", marginTop: 16, background: "rgba(34,197,94,0.08)", borderRadius: 9, padding: "10px 20px", fontWeight: 500 }}>
                  {t('profilepage.cancelSuccess')}<br />
                  {t('profilepage.cancelInfo')}
                </div>
              )}
              {cancelError && (
                <div className="info-message" style={{ color: "#f87171", marginTop: 16, background: "rgba(248,113,113,0.08)", borderRadius: 9, padding: "10px 20px", fontWeight: 500 }}>
                  ❌ {cancelError}
                </div>
              )}
            </div>

            
          )}
        
        </div>
      </div>
    </>
  );
}

export default Profile;
